import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { GTCheckBox, GTColumnSelectAnchor, GTExpander } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { GTInternalIds } from '../types';
import { gTCheckHandler } from '../util';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { Link } from 'react-router-dom';
import { TableIds } from 'src/api/types/misc';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { TextOverflowTooltip } from 'src/common';

export interface ScenarioLibraryItem {
  id: string;
  name?: string | null;
  author?: string | null;
  description?: string | null;
  assessmentId?: string | null;
  source?: number | null;
  event?: number | null;
  consequence?: number | null;
  riskNarrative?: string | null;
  frequencyTimes?: number | null;
  frequencyYears?: number | null;
  frequencyNote?: string | null;
  expectedLoss?: number | null;
  managementStrategy?: string | null;
  lowerFormula?: string | null;
  upperFormula?: string | null;
  note?: string | null;
  groupName?: string | null;
}

type UseColumns = (a: {
  data?: ScenarioLibraryItem[];
  isCheckerEnabled?: boolean;
  tableId: TableIds;
  openNewTab?: boolean;
}) => {
  columns: ColumnDef<ScenarioLibraryItem>[];
  checked: string[];
  setChecked: (a: string[]) => void;
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = ['author', 'name', 'groupName', 'description'];

export const useScenarioLibraryColumns: UseColumns = ({ data = [], isCheckerEnabled, tableId, openNewTab = true }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${tableId}-columns`, defaultColumns);
  const [checked, setChecked] = useState<string[]>([]);

  const columns = useMemo(() => {
    let internalIds = [GTInternalIds.expander, GTInternalIds.tdm];

    if (isCheckerEnabled) {
      internalIds.push(GTInternalIds.checker);
    } else {
      internalIds = internalIds.filter((el) => el !== GTInternalIds.checker);
      if (checked.length > 0) setChecked([]);
    }

    const list: ColumnDef<ScenarioLibraryItem>[] = [
      {
        id: GTInternalIds.checker,
        header: ({ table }) => {
          const { isAllChecked, checkAllHandler } = gTCheckHandler(
            {
              checked,
              selector: 'id',
              table,
            },
            setChecked,
          );

          return <GTCheckBox isChecked={isAllChecked} onClick={checkAllHandler} />;
        },
        cell: ({ row }) => {
          const id = row.original.id;
          return (
            <GTCheckBox
              isChecked={checked.includes(id)}
              onClick={() => {
                if (checked.includes(id)) {
                  setChecked(checked.filter((el) => el !== id));
                } else {
                  setChecked([...checked, id]);
                }
              }}
            />
          );
        },
        size: 1,
        maxSize: 35,
        enableSorting: false,
      },
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'author',
        header: 'Author',
        accessorFn: (row) => row.author || null,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name || null,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              className="table-link"
              to={
                info.row.original.assessmentId && info.row.original.assessmentId !== 'TEMPLATE'
                  ? `/risk-assessments/${info.row.original.assessmentId}/scenario/${info.row.original.id}`
                  : `/scenario-library/templates/${info.row.original.id}`
              }
              target={
                info.row.original.assessmentId && info.row.original.assessmentId !== 'TEMPLATE'
                  ? '_self'
                  : openNewTab
                    ? '_blank'
                    : '_self'
              }
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: {
                    value: ['Scenario Template'],
                  },
                  destination: {
                    value:
                      info.row.original.assessmentId && info.row.original.assessmentId !== 'TEMPLATE'
                        ? ['Scenario']
                        : ['Scenario Template Detail'],
                    params: {
                      id: info.row.original.id,
                    },
                  },
                  tags:
                    info.row.original.assessmentId && info.row.original.assessmentId !== 'TEMPLATE'
                      ? ['SCENARIO']
                      : ['SCENARIO TEMPLATE'],
                });
              }}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'description',
        header: 'Description',
        accessorFn: (row) => row.description || null,
        cell: (info) => <TextOverflowTooltip>{info.getValue() as string}</TextOverflowTooltip>,
        sortingFn: sortingFns.text,
      },
      {
        id: 'groupName',
        header: 'Group',
        accessorFn: (row) => row.groupName || null,
        cell: (info) => <TextOverflowTooltip>{info.getValue() as string}</TextOverflowTooltip>,
        sortingFn: sortingFns.text,
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: () => null,
        size: 1,
        maxSize: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, checked, isCheckerEnabled]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={tableId}
      options={[
        { key: 'author', title: 'Author' },
        { key: 'name', title: 'Name' },
        { key: 'groupName', title: 'Group' },
        { key: 'description', title: 'Description' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return { columns, checked, setChecked, GTColumnSelectAnchorExported };
};
